import { forwardRef, useId } from 'react';
import PropTypes from 'prop-types';

import { getCourseBackground } from './catalog';
import './courseBackgrounds.css';

/**
 * The decorative band behind a course/subject title.
 *
 * Renders three stacked layers — accent gradient, tiled motif, right-pinned
 * hero glyph — plus a left-to-right scrim that keeps white text readable over
 * the lighter accents. Children are laid out above all of it.
 *
 * The band is purely decorative, so every layer is `aria-hidden`; the course
 * name inside `children` carries the meaning.
 *
 * Refs are forwarded because antd wrappers (Dropdown, Tooltip, Popover) attach
 * one to their trigger child, and a timetable cell is exactly that.
 */
const CourseBackground = forwardRef(
  ({ themeKey, className, style, variant, children, ...rest }, ref) => {
    const theme = getCourseBackground(themeKey);
    // useId gives each instance its own <pattern> id: two cards sharing one id
    // would be invalid markup, and duplicate ids break the fill lookup in Safari.
    const patternId = `course-bg-${useId().replace(/:/g, '')}`;
    const variantClass = variant === 'subtle' ? ' course-bg--subtle' : '';

    return (
      <div
        ref={ref}
        className={`course-bg${variantClass}${className ? ` ${className}` : ''}`}
        style={{
          background: `linear-gradient(135deg, ${theme.color} 0%, ${theme.colorDeep} 100%)`,
          ...style,
        }}
        {...rest}
      >
        <svg className="course-bg__texture" aria-hidden="true" focusable="false">
          <defs>
            <pattern id={patternId} width="48" height="48" patternUnits="userSpaceOnUse">
              {theme.tile}
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#${patternId})`} />
        </svg>

        {theme.hero && (
          <svg
            className="course-bg__hero"
            viewBox="0 0 120 100"
            preserveAspectRatio="xMaxYMid meet"
            aria-hidden="true"
            focusable="false"
          >
            {theme.hero}
          </svg>
        )}

        <span className="course-bg__scrim" aria-hidden="true" />

        {children != null && <div className="course-bg__content">{children}</div>}
      </div>
    );
  },
);

CourseBackground.displayName = 'CourseBackground';

CourseBackground.propTypes = {
  /** Catalogue key; anything unknown or null renders the neutral default. */
  themeKey: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  /**
   * `subtle` dims the artwork for small, text-dense surfaces like timetable
   * cells, where a full-strength watermark competes with 11px room labels.
   */
  variant: PropTypes.oneOf(['default', 'subtle']),
  children: PropTypes.node,
};

CourseBackground.defaultProps = {
  themeKey: null,
  className: '',
  style: undefined,
  variant: 'default',
  children: null,
};

export default CourseBackground;
